import { Text, View } from "react-native";
import { Button } from "ui/gluestack/button";
import { AddIcon, Icon } from "ui/gluestack/icon";
import { FormControlledInput } from "ui/molecules/form/FormControlledInput";
import { FormControlledMultiSelectDropdown } from "ui/molecules/form/FormControlledMultiSelectDropdown";
import LoadingSpinner from "ui/atoms/spinner/LoadingSpinner";
import {
  emailValidation,
  maxLengthValidation,
  minLengthValidation,
  requiredValidation,
  validationSchema,
} from "ui/utils/Validators";
import { CreateUserRequest } from "app/services/api/types/residents/requests/CreateUserRequest";
import { useHandleUserForm } from "../hooks/useHandleUserForm";
import { AuthUserModel } from "app/services/api/types/authorisation/models/AuthUserModel";
import { useRolesDropdownOptions } from "../hooks/useRolesDropdownOptions";
import FormControlledLocationSelector from "app/components/molecules/FormControlledLocationSelector";

interface Props {
  onClose: () => void;
  onSuccess?: () => void;
  isEditing?: boolean;
  initialUser?: AuthUserModel;
}

export default function AddUserForm({
  onClose,
  onSuccess,
  isEditing,
  initialUser,
}: Props) {
  const { rolesOptions, isLoading: isLoadingRoles } = useRolesDropdownOptions();
  const { control, isLoadingAction, submitForm } = useHandleUserForm(
    onSuccess,
    initialUser,
  );

  if (isLoadingAction || isLoadingRoles) {
    return (
      <View className="flex-1 justify-center items-center my-10">
        <LoadingSpinner />
      </View>
    );
  }

  return (
    <View>
      <View className="lg:grid lg:grid-cols-2 gap-4 w-full">
        <FormControlledInput<CreateUserRequest>
          id="first_name"
          name="first_name"
          type="text"
          control={control}
          label="First name"
          placeholder="Enter user's first name"
          isRequired
          isDisabled={isLoadingAction}
          rules={{
            minLength: minLengthValidation(validationSchema.name.minLength),
            required: requiredValidation("First name"),
          }}
          onSubmitEditing={submitForm}
        />
        <FormControlledInput<CreateUserRequest>
          id="last_name"
          name="last_name"
          type="text"
          control={control}
          label="Last name"
          placeholder="Enter user's last name"
          isRequired
          isDisabled={isLoadingAction}
          rules={{
            minLength: minLengthValidation(validationSchema.name.minLength),
            required: requiredValidation("Last name"),
          }}
          onSubmitEditing={submitForm}
        />
      </View>
      <View className="lg:grid lg:grid-cols-2 gap-4 w-full mt-4">
        <FormControlledInput<CreateUserRequest>
          id="email"
          name="email"
          type="text"
          control={control}
          label="Email"
          placeholder="Enter user's email"
          isRequired
          isDisabled={isLoadingAction || isEditing}
          rules={{
            maxLength: maxLengthValidation(validationSchema.email.maxLength),
            minLength: minLengthValidation(validationSchema.email.minLength),
            required: requiredValidation("Email"),
            validate: {
              emailValidation,
            },
          }}
          onSubmitEditing={submitForm}
        />

        <FormControlledMultiSelectDropdown<CreateUserRequest>
          name="roles"
          control={control}
          label="Role"
          placeholder="Select roles"
          isRequired
          isDisabled={isLoadingAction}
          options={rolesOptions}
          rules={{
            required: requiredValidation("Role"),
          }}
        />
      </View>
      <View className="lg:grid lg:grid-cols-2 gap-4 w-full mt-4 -z-10">
        <FormControlledLocationSelector<CreateUserRequest>
          name="locations"
          control={control}
          label="Location(s)"
          multiselect
        />
      </View>
      {!isEditing ? (
        <View className="mt-4">
          <Text>
            Note that user invitations will only be valid for 24 hours
          </Text>
        </View>
      ) : null}
      <View className="flex flex-row justify-end gap-x-4 mt-6 -z-20">
        <Button action="primary" size="sm" variant="outline" onPress={onClose}>
          <Text className="text-primary-500 font-semibold">Cancel</Text>
        </Button>
        <Button action="primary" size="sm" onPress={submitForm}>
          {isEditing ? (
            <Text className="text-white">Save changes</Text>
          ) : (
            <>
              <Text className="text-white">Create user</Text>
              <Icon as={AddIcon} className="w-4 h-4 stroke-white" />
            </>
          )}
        </Button>
      </View>
    </View>
  );
}
